<template>
	<v-row class="full-height sample-orders-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="sample-orders-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="3" lg="2">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search sample orders..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="sample-orders-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Status Filter -->
						<v-col cols="6" sm="3" md="2">
							<v-select
								v-model="selectedFilters"
								:items="filterOptions"
								label="Order Status"
								multiple
								clearable
								outlined
								dense
								hide-details
								@change="onFilterChange"
								@click:clear="onFilterClear"
							></v-select>
						</v-col>

						<!-- Return Status Filter -->
						<v-col cols="6" sm="3" md="2">
							<v-select
								v-model="returnStatusFilter"
								:items="returnStatusOptions"
								label="Return Status"
								clearable
								outlined
								dense
								hide-details
								@change="onFilterChange"
								@click:clear="onFilterClear"
							></v-select>
						</v-col>

						<!-- Sales Rep Filter (Admin only) -->
						<v-col cols="6" sm="3" md="2" v-if="canFilterByRep">
							<v-select
								v-model="selectedSalesRepIds"
								:items="salesRepOptions"
								item-text="name"
								item-value="salesRepId"
								label="Sales Rep"
								multiple
								clearable
								outlined
								dense
								hide-details
								@change="onFilterChange"
								@click:clear="onFilterClear"
							>
								<template v-slot:selection="{ item, index }">
									<span v-if="index === 0" class="text-truncate">{{ item.name }}</span>
									<span v-if="index === 1" class="grey--text caption ml-1">
										+{{ selectedSalesRepIds.length - 1 }}
									</span>
								</template>
							</v-select>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 sample-orders-table-container">
			<v-data-table
				:headers="headers"
				:items="orders"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="sample-orders-data-table"
				@click:row="viewOrder"
				must-sort
				fixed-header
			>
				<!-- Organization Column -->
				<template v-slot:item.organization="{ item }">
					<div class="d-flex align-center">
						<truncate-tooltip>{{ item.customer.customer.organization }}</truncate-tooltip>
						<v-btn
							v-if="canEditCustomer"
							icon
							x-small
							class="ml-1 edit-customer-btn"
							@click.stop="navigateToCustomer(item.customer.customerId)"
							title="Edit Customer"
						>
							<v-icon x-small>mdi-open-in-new</v-icon>
						</v-btn>
					</div>
				</template>

				<!-- Order Date Column -->
				<template v-slot:item.orderDate="{ item }">
					{{ formatDate(item.orderDate) }}
				</template>

				<!-- Sales Rep Column -->
				<template v-slot:item.salesRepId="{ item }">
					{{ repSalesRepInitials(item.salesRepId) }}
				</template>

				<!-- Status Column -->
				<template v-slot:item.status="{ item }">
					<div class="table-status" :class="renderStatus(item.status).color">
						{{ renderStatus(item.status).label }}
					</div>
				</template>

				<!-- Return Status Column -->
				<template v-slot:item.returnStatus="{ item }">
					<div v-if="item.isReturned" class="table-status table-status-green">
						Returned
						<div class="return-date">{{ formatDate(item.returnedAt) }}</div>
					</div>
					<v-btn
						v-else-if="canMarkReturned && item.status === 'SHIPPED'"
						small
						color="primary"
						@click.stop="markAsReturned(item)"
						:loading="markingReturned === item.salesOrderId"
					>
						Mark Returned
					</v-btn>
					<span v-else class="grey--text">--</span>
				</template>

				<!-- Contact Column -->
				<template v-slot:item.primaryContact="{ item }">
					<truncate-tooltip v-if="item.customer.contact">
						{{ item.customer.contact.firstName }} {{ item.customer.contact.lastName }}
					</truncate-tooltip>
				</template>

				<!-- Email Column -->
				<template v-slot:item.email="{ item }">
					<truncate-tooltip v-if="item.customer.contact && item.customer.contact.email">
						{{ item.customer.contact.email }}
					</truncate-tooltip>
				</template>

				<!-- Ref # Column -->
				<template v-slot:item.referenceNumber="{ item }">
					<truncate-tooltip v-if="item.referenceNumber">
						{{ item.referenceNumber }}
					</truncate-tooltip>
				</template>

				<!-- Total Column -->
				<template v-slot:item.total="{ item }">
					{{ renderPrice(item.computedTotal ?? item.total) }}
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-file-document-outline</v-icon>
						<p class="mt-2 grey--text">No sample orders found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<!-- Mark as Returned Confirmation Dialog -->
		<v-dialog
			v-model="confirmReturnDialog"
			max-width="450"
			persistent
		>
			<v-card>
				<v-card-title class="headline">
					Confirm Return
				</v-card-title>
				<v-card-text style="font-size: 1.1rem;">
					<p class="mb-4">
						Mark samples for order <strong>{{ orderToReturn?.orderNumber }}</strong> as returned?
					</p>
					<v-checkbox
						v-model="createReplacementQuote"
						label="Create quote for replacement order"
						hide-details
						class="mt-0"
					></v-checkbox>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn
						text
						@click="cancelReturnDialog"
					>
						Cancel
					</v-btn>
					<v-btn
						color="primary"
						@click="confirmMarkAsReturned"
						:loading="markingReturned !== null"
					>
						Mark Returned
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>
import queries from "../../gql/queries";
import { renderPrice } from "@/helpers/PricingCalculator";
import { SystemPermissions, WellKnownRoles } from "@/models/User";
import TruncateTooltip from "@/components/TruncateTooltip.vue";
export default {
	name: "SampleOrdersList",
	components: { TruncateTooltip },
	data() {
		return {
			canEditCustomer: false,
			canFilterByRep: false,
			canMarkReturned: false,
			loading: true,
			searchForm: {
				keywords: "",
			},
			orders: [],
			salesReps: [],
			totalCount: 0,
			searchTimeout: null,
			selectedSalesRepIds: [],
			filterOptions: [
				{ text: "In Process", value: "IN_PROCESS" },
				{ text: "Shipped", value: "SHIPPED" },
			],
			selectedFilters: [],
			returnStatusFilter: null,
			returnStatusOptions: [
				{ text: "Returned", value: true },
				{ text: "Not Returned", value: false },
			],
			markingReturned: null,
			confirmReturnDialog: false,
			orderToReturn: null,
			createReplacementQuote: false,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["modifiedAt"],
				sortDesc: [true],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "School/Organization",
					value: "organization",
					sortable: true,
					width: "14%",
				},
				{
					text: "Order #",
					value: "orderNumber",
					sortable: true,
					width: "6%",
				},
				{
					text: "Order Date",
					value: "orderDate",
					sortable: true,
					width: "8%",
				},
				{
					text: "Rep",
					value: "salesRepId",
					sortable: true,
					width: "4%",
				},
				{
					text: "Status",
					value: "status",
					sortable: true,
					width: "8%",
					align: "center",
				},
				{
					text: "Contact",
					value: "primaryContact",
					sortable: true,
					width: "10%",
				},
				{
					text: "Email",
					value: "email",
					sortable: true,
					width: "12%",
				},
				{
					text: "Return Status",
					value: "returnStatus",
					sortable: false,
					width: "12%",
					align: "center",
				},
				{
					text: "Ref #",
					value: "referenceNumber",
					sortable: true,
					width: "9%",
				},
				{
					text: "Order Total",
					value: "total",
					sortable: true,
					width: "7%",
					align: "end", // money reads right-aligned (#357)
				},
			],
		};
	},
	apollo: {
		ordersQuery: {
			query: () => queries.SEARCH_ORDERS,
			variables() {
				return this.buildQueryVariables();
			},
			update(data) {
				this.loading = false;
				this.totalCount = data.salesOrders.totalCount;
				this.orders = data.salesOrders.items;
				return data.salesOrders;
			},
			error(error) {
				console.error("Error fetching sample orders:", error);
				this.loading = false;
			},
		},
		salesReps: {
			query: queries.LIST_SALES_REPS,
			variables: {
				take: 500,
				offset: 0,
			},
			fetchPolicy: "no-cache",
			update: (data) => data.salesReps.items,
		},
	},
	computed: {
		salesRepOptions() {
			if (!this.salesReps) return [];
			return [...this.salesReps.filter((rep) => !rep.inactiveDate)];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
	},
	methods: {
		renderPrice,
		navigateToCustomer(customerId) {
			if (customerId) {
				this.$router.push(`/relationships/customer/${customerId}`);
			}
		},
		formatDate(date) {
			if (!date) return "";
			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},
		renderStatus(statusId) {
			let status = {
				label: "",
				color: "",
			};
			switch (statusId) {
				case "SHIPPED":
					status.label = "Shipped";
					status.color = "table-status-green";
					break;
				case "CANCELED":
				case "CANCELLED":
					status.label = "Canceled";
					status.color = "table-status-red";
					break;
				default:
					status.label = "In Process";
					status.color = "table-status-orange";
			}
			return status;
		},
		repSalesRepInitials(salesRepId) {
			const repById = this.salesReps.find((obj) => obj.salesRepId == salesRepId);

			if (!repById) return "";

			const tokens = repById.name.split(/\s+/).filter((token) => token.length > 0);

			if (tokens.length === 1) {
				return tokens[0];
			}

			const initials = tokens.map((token) => token[0].toUpperCase());
			return initials.join("");
		},
		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},
		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},
		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},
		onFilterClear() {
			this.$nextTick(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			});
		},
		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				organization: "customer.customer.organization",
				orderNumber: "orderNumber",
				orderDate: "orderDate",
				salesRepId: "salesRepId",
				status: "status",
				primaryContact: "customer.contact.lastName",
				email: "customer.contact.email",
				referenceNumber: "referenceNumber",
				total: "total",
				modifiedAt: "modifiedAt",
			};
			return sortFieldMap[columnValue] || "modifiedAt";
		},

		// Build nested sort object from dot notation (e.g., "customer.customer.organization")
		buildSortObject(fieldPath, direction) {
			const parts = fieldPath.split(".");
			if (parts.length === 1) {
				return { [parts[0]]: direction };
			}
			// Build nested object from inside out
			let result = { [parts[parts.length - 1]]: direction };
			for (let i = parts.length - 2; i >= 0; i--) {
				result = { [parts[i]]: result };
			}
			return result;
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build where clause - ALWAYS filter for sample orders only
			const where = {
				and: [
					// This is the key filter for sample orders
					{ isSampleOrder: { eq: true } },
					{
						or: [
							{ referenceNumber: { contains: keyword } },
							{ orderNumber: { contains: keyword } },
							{ customer: { customer: { organization: { contains: keyword } } } },
							{ customer: { customer: { customerNumber: { contains: keyword } } } },
							{ customer: { contact: { firstName: { contains: keyword } } } },
							{ customer: { contact: { lastName: { contains: keyword } } } },
							{ customer: { contact: { email: { contains: keyword } } } },
						],
					},
				],
			};

			// Add status filter conditions
			if (this.selectedFilters && this.selectedFilters.length > 0) {
				where.and.push({
					or: this.selectedFilters.map((filter) => ({ status: { eq: filter } })),
				});
			}

			// Add return status filter
			if (this.returnStatusFilter !== null) {
				where.and.push({ isReturned: { eq: this.returnStatusFilter } });
			}

			// Add sales rep filter
			if (this.selectedSalesRepIds && this.selectedSalesRepIds.length > 0) {
				where.and.push({
					salesRepId: { in: this.selectedSalesRepIds },
				});
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "modifiedAt";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldPath = this.getSortFieldName(sortColumn);
			const order = [this.buildSortObject(sortFieldPath, sortDirection)];

			// Determine if we should include all reps' sample orders
			// For sales reps: include all reps when they've selected a rep filter OR when searching with keywords
			// For admins: always include all reps
			const user = this.$store.getters.userInfo;
			const isAdmin = this.$userService.isAdmin(user);
			const isSalesRep = this.$userService.hasRole(user, WellKnownRoles.REP);
			const hasRepFilterSelected = Boolean(this.selectedSalesRepIds && this.selectedSalesRepIds.length > 0);
			const hasSearchKeyword = Boolean(keyword && keyword.length > 0);
			const includeAllReps = Boolean(isAdmin || (isSalesRep && (hasRepFilterSelected || hasSearchKeyword)));

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				searchTerm: "",
				where,
				includeAllReps,
			};
		},
		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.ordersQuery) {
				await this.$apollo.queries.ordersQuery.refetch();
			}
		},
		viewOrder(orderData) {
			// Navigate to the sample order view using router
			this.$router.push(`/sales-orders/sample-orders/${orderData.salesOrderId}`);
		},
		markAsReturned(order) {
			// Show confirmation dialog instead of browser confirm
			this.orderToReturn = order;
			this.createReplacementQuote = false;
			this.confirmReturnDialog = true;
		},
		cancelReturnDialog() {
			this.confirmReturnDialog = false;
			this.orderToReturn = null;
			this.createReplacementQuote = false;
		},
		async confirmMarkAsReturned() {
			if (!this.orderToReturn) return;

			this.markingReturned = this.orderToReturn.salesOrderId;
			try {
				// Mark the sample order as returned
				await this.$http.post(`/api/orders/${this.orderToReturn.salesOrderId}/mark-returned`);
				this.$toast.success("Sample order marked as returned");

				// If user wants a replacement quote, create it and navigate
				if (this.createReplacementQuote) {
					try {
						const response = await this.$http.post(
							`/api/orders/${this.orderToReturn.salesOrderId}/create-replacement-quote`
						);
						this.$toast.success("Replacement quote created");
						this.confirmReturnDialog = false;
						this.orderToReturn = null;
						this.createReplacementQuote = false;
						// Navigate to the new quote
						this.$router.push(`/sales-orders/quotes/${response.data.quoteId}`);
						return;
					} catch (quoteError) {
						const errorMessage = quoteError.response?.data?.error || "Failed to create replacement quote";
						this.$toast.error(errorMessage);
						// Still refresh the list since the return was marked successfully
					}
				}

				this.confirmReturnDialog = false;
				this.orderToReturn = null;
				this.createReplacementQuote = false;
				await this.fetchData();
			} catch (error) {
				const errorMessage = error.response?.data?.error || "Failed to mark as returned";
				this.$toast.error(errorMessage);
			} finally {
				this.markingReturned = null;
			}
		},
	},
	async mounted() {
		this.loading = true;
		const user = this.$store.getters.userInfo;
		this.canEditCustomer =
			this.$userService.isAdmin(user) ||
			this.$userService.hasPermission(user, SystemPermissions.Relationships_Customer_ALL);
		// Only admin users can filter by sales rep (reps should only see their own sample orders)
		this.canFilterByRep = this.$userService.isAdmin(user);
		this.canMarkReturned =
			this.$userService.isAdmin(user) ||
			this.$userService.hasPermission(user, SystemPermissions.SalesOrders_SampleOrder_MarkReturned);
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.sample-orders-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.sample-orders-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.sample-orders-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.sample-orders-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.return-date {
	font-size: 1rem;
	color: $color-gray-dark;
	margin-top: 2px;
}

.sample-orders-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;

		table {
			table-layout: fixed;
			width: 100%;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}
	// Make rows clickable
	::v-deep tbody tr {
		cursor: pointer;
		transition: background-color 0.2s;

		&:hover {
			background-color: $color-gray-light !important;
		}
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}

	// Edit customer button - visible on row hover
	.edit-customer-btn {
		opacity: 0;
		transition: opacity 0.2s;
	}

	::v-deep tbody tr:hover .edit-customer-btn {
		opacity: 1;
	}

	// Status badge styling (reuse existing)
	.table-status {
		display: inline-block;
		border: 1px solid;
		border-radius: 3px;
		font-size: 1.4rem;
		padding: 0.5rem;
		min-width: 9rem;
		text-align: center;

		&-orange {
			border-color: $color-orange;
			color: $color-orange;
		}

		&-red {
			border-color: red;
			color: red;
		}

		&-green {
			border-color: $color-green;
			color: $color-green;
		}

		&-blue {
			border-color: $color-blue;
			color: $color-blue;
		}

		&-gray {
			border-color: $color-gray-dark;
			color: $color-gray-dark;
		}
	}
}
</style>
